import { $, CustomEventWithDetail, type Html, html, on, rune, View } from 'rune-ts';
import klass from './FormDropDown.module.scss';
import { typoPcMo } from '../../../typography/typo';
import { FORM_DROPDOWN_CONSTANT } from './type';
import { classes } from '../../../util';
import { head, isNil, isUndefined } from '@fxts/core';
import { DropdownDownIcon, DropdownUpIcon } from '../../../../renderApp/components/atoms/Icon';

export class NotifyToggleEvent extends CustomEventWithDetail<{ is_open: boolean }> {}

export interface DropdownItem<T = unknown> {
  key: string; // unique
  name?: string | Html;
  value?: T;
  hidden?: boolean;
}

export type DropdownItemInput<T = unknown> = string | Partial<DropdownItem<T>>;

export interface FormDropdownData<T = unknown> {
  name: string;
  status:
    | typeof FORM_DROPDOWN_CONSTANT.ACTIVE
    | typeof FORM_DROPDOWN_CONSTANT.INACTIVE
    | typeof FORM_DROPDOWN_CONSTANT.DISABLED
    | typeof FORM_DROPDOWN_CONSTANT.READ_ONLY;
  items: DropdownItemInput<T>[];
  selected_item?: DropdownItem<T>;
  selected_key?: string;
  placeholder?: string;
}

export interface FormDropdownOption<T = unknown> {
  klass?: string;

  // 드랍다운 컴포넌트 자체가 아닌 드랍다운 되는 컨테이너 영역 클래스명
  dropdown_container_klass?: string;
  dropdown_item_klass?: string;
  dropdown_button_wrap_klass?: string;
  is_mobile?: boolean;
  onSelectHook?: (selectedItem: DropdownItem<T>) => void;

  /**
   * - 기본 제공 되는 toggle 이벤트가 울리지 않습니다.
   * - 직접 제어하고 싶을 때 true로 설정 해주세요. (setOpen 활용)
   * - 드롭다운이 열린 상태에서 아이템을 클릭할 경우 모든 동작을 하지만 redraw를 하지 않아 닫히지 않습니다.
   */
  passive_mode?: boolean;
  size?: 'large' | 'small';
}

interface FormDropDownState<T = unknown> extends Omit<FormDropdownData<T>, 'items'>, FormDropdownOption<T> {
  klass: string;
  dropdown_container_klass: string;
  dropdown_item_klass: string;
  dropdown_button_wrap_klass: string;
  is_mobile: boolean;
  is_open: boolean;
  items: DropdownItem<T>[];
  selected_item: DropdownItem<T>;
  onSelectHook?: (selectedItem: DropdownItem<T>) => void;
  size: 'large' | 'small';
}

export class FormDropDownAfterRedrawEvent extends CustomEvent<never> {}

// 문자열이나 부분 DropdownItem을 완전한 DropdownItem으로 변환하는 유틸리티 함수
export function createDropdownItems<T = unknown>(items: DropdownItemInput<T>[]): DropdownItem<T>[] {
  return items.map((item) => {
    if (typeof item === 'string') {
      // 문자열인 경우
      return {
        key: item,
        name: item,
        value: item as unknown as T,
      };
    } else {
      // 객체인 경우 (key만 필수)
      if (isNil(item.key)) {
        throw new Error('DropdownItem은 key 속성이 필수입니다.');
      }

      return {
        key: item.key,
        name: item.name ?? item.key,
        value: isUndefined(item.value) ? (item.key as unknown as T) : item.value,
        hidden: item.hidden,
      };
    }
  });
}

export class FormDropdown<T = unknown> extends View<FormDropdownData<T>> {
  state: FormDropDownState<T>;

  static AfterRedrawEvent = FormDropDownAfterRedrawEvent;

  constructor(data: FormDropdownData<T>, option: FormDropdownOption<T> = {}) {
    super(data, option);

    const is_mobile = option.is_mobile || !!rune.getSharedData(this)?.is_mobile || false;

    // 아이템 처리
    const processedItems = createDropdownItems<T>(data.items);

    // 선택된 아이템 결정
    let selected_item: DropdownItem<T>;
    if (data.selected_item) {
      selected_item = data.selected_item;
    } else if (data.selected_key) {
      selected_item = processedItems.find((item) => item.key === data.selected_key) ||
        head(processedItems) || { key: 'no item', name: 'no item', value: 'no item' as unknown as T };
    } else if (data.placeholder) {
      selected_item = {
        key: 'no item',
        name: '',
        value: null as unknown as T,
      };
    } else {
      selected_item = head(processedItems) || {
        key: 'no item',
        name: 'no item',
        value: 'no item' as unknown as T,
      };
    }

    this.state = {
      klass: option.klass ?? '',
      dropdown_container_klass: option.dropdown_container_klass ?? '',
      dropdown_item_klass: option.dropdown_item_klass ?? '',
      dropdown_button_wrap_klass: option.dropdown_button_wrap_klass ?? '',
      is_mobile,
      name: data.name,
      status: data.status,
      items: processedItems,
      is_open: false,
      selected_item,
      onSelectHook: option.onSelectHook,
      passive_mode: option.passive_mode,
      size: option.size || is_mobile ? 'small' : 'large',
      placeholder: data.placeholder,
    };

    if (this.state.placeholder && !this.state.selected_item.name) {
      this.state.status = FORM_DROPDOWN_CONSTANT.INACTIVE;
    }
  }

  override template() {
    return html`
      <div
        class="${this.state.klass} ${classes(klass.form_dropdown, {
          [klass.large]: this.state.size === 'large',
          [klass.small]: this.state.size === 'small',
        })}"
        data-error="false"
      >
        <div class="${klass.dropdown_wrapper}">
          <input type="hidden" name="${this.state.name}" value="${this.state.selected_item.value}" />

          <div
            class="${this.state.dropdown_button_wrap_klass} ${classes(klass.dropdown_button_wrap, {
              [klass.inactive]: this.state.status === FORM_DROPDOWN_CONSTANT.INACTIVE,
              [klass.active]: this.state.status === FORM_DROPDOWN_CONSTANT.ACTIVE,
              [klass.disabled]: this.state.status === FORM_DROPDOWN_CONSTANT.DISABLED,
              [klass.readonly]: this.state.status === FORM_DROPDOWN_CONSTANT.READ_ONLY,
            })}"
          >
            <button
              class="${klass.dropdown_button} ${klass.unstyled_button} ${typoPcMo({
                is_mobile: this.state.is_mobile,
                pc: this.state.size === 'large' ? '16_medium' : '14_medium',
                mo: '14_medium',
              })}"
              type="button"
              aria-haspopup="listbox"
              aria-expanded="${this.state.is_open}"
            >
              ${this.data.placeholder && !this.state.selected_item.name
                ? this.data.placeholder
                : this.state.selected_item.name}
            </button>

            <div class="${klass.dropdown_icon}">${DropdownDownIcon}</div>
          </div>

          <div
            class="${this.state.dropdown_container_klass} ${classes(klass.dropdown_container, {
              [klass.is_open]: this.state.is_open,
            })}"
            role="listbox"
          >
            <ul class="${klass.dropdown_content}">
              ${this.state.items.map(({ key, value, name, hidden }) => {
                if (hidden) return '';

                return html`
                  <li data-key="${key}" data-value="${value}" role="option">
                    <button
                      class="${klass.unstyled_button} ${klass.dropdown_item} ${this.state
                        .dropdown_item_klass} ${typoPcMo({
                        is_mobile: this.state.is_mobile,
                        pc: this.state.size === 'large' ? '16_medium' : '14_medium',
                        mo: '14_medium',
                      })}"
                    >
                      ${name}
                    </button>
                  </li>
                `;
              })}
            </ul>
          </div>
        </div>
      </div>
    `;
  }

  private refresh(): this {
    this.updateStatusClasses();

    // 드롭다운 상태에 따른 클래스 토글
    const container = this.element().querySelector(`.${klass.dropdown_container}`);
    const wrapper = this.element().querySelector(`.${klass.dropdown_wrapper}`);

    if (this.state.is_open) {
      container?.classList.add(klass.is_open);
      wrapper?.classList.add(klass.is_open);
      // 화살표를 위쪽으로 변경
      $(this.element()).find(`.${klass.dropdown_icon}`)?.setInnerHtml(DropdownUpIcon.make(this));
    } else {
      container?.classList.remove(klass.is_open);
      wrapper?.classList.remove(klass.is_open);
      // 화살표를 아래쪽으로 변경
      $(this.element()).find(`.${klass.dropdown_icon}`)?.setInnerHtml(DropdownDownIcon.make(this));
    }

    // 버튼 텍스트 업데이트
    $(this.element()).find(`.${klass.dropdown_button}`)?.setInnerHtml(this.getSelectedItemName());

    // input 값 갱신
    const inputElement = this.element().querySelector(`input[name="${this.state.name}"]`);
    if (inputElement) {
      inputElement.setAttribute('value', String(this.state.selected_item.value));
    }

    // aria-expanded 속성 업데이트
    const buttonElement = this.element().querySelector(`.${klass.dropdown_button}`);
    if (buttonElement) {
      buttonElement.setAttribute('aria-expanded', String(this.state.is_open));
    }

    return this;
  }

  @on('click', `.${klass.dropdown_button_wrap}`)
  private toggleDropDown() {
    if (!this.isInteractive()) return;
    if (this.state.passive_mode) return;

    const newIsOpen = !this.state.is_open;
    this.setState({ is_open: newIsOpen });
  }

  @on('click', `.${klass.dropdown_item}`)
  private selectItem(e: Event) {
    if (!this.isInteractive()) return;

    this.state.status = FORM_DROPDOWN_CONSTANT.ACTIVE;

    $(e.currentTarget)
      .closest('li')
      ?.chain((el) => {
        const key = el.getAttribute('data-key');
        const selected_item = this.state.items.find((item) => item.key === key);

        if (selected_item) {
          if (this.state.onSelectHook) {
            this.state.onSelectHook(selected_item);
          }

          if (this.state.passive_mode) return;

          this.setState({
            selected_item,
            is_open: false,
          });
        }

        if (this.state.passive_mode) return;
        this.refresh();
      });
  }

  private updateStatusClasses() {
    const element = this.element().querySelector<HTMLElement>(`.${klass.dropdown_button_wrap}`);

    if (element) {
      element.className = `${this.state.dropdown_button_wrap_klass} ${classes(klass.dropdown_button_wrap, {
        [klass.inactive]: this.state.status === FORM_DROPDOWN_CONSTANT.INACTIVE,
        [klass.active]: this.state.status === FORM_DROPDOWN_CONSTANT.ACTIVE,
        [klass.disabled]: this.state.status === FORM_DROPDOWN_CONSTANT.DISABLED,
        [klass.readonly]: this.state.status === FORM_DROPDOWN_CONSTANT.READ_ONLY,
      })}`;
    }
  }

  markMethodsAsUsed() {
    const noop = (...args: unknown[]) => {
      return args;
    };
    noop(this.selectItem, this.toggleDropDown);
  }

  private isInteractive(): boolean {
    return (
      this.state.status === FORM_DROPDOWN_CONSTANT.ACTIVE ||
      this.state.status === FORM_DROPDOWN_CONSTANT.INACTIVE
    );
  }

  private setState(newState: Partial<FormDropDownState<T>>) {
    this.state = { ...this.state, ...newState };
    this.refresh();
    this.dispatchEvent(FormDropdown.AfterRedrawEvent, { bubbles: true });
  }

  handleClickOutside = (event: MouseEvent | TouchEvent) => {
    if (this.isOpen() && !this.element().contains(event.target as Node)) {
      this.setOpen(false);
    }
  };

  protected override onMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    document.addEventListener('touchstart', this.handleClickOutside);
  }

  protected override onUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    document.removeEventListener('touchstart', this.handleClickOutside);
  }

  // 기존 공개 메서드들은 그대로 유지
  public setItems(newItems: DropdownItemInput<T>[], selectedKey?: string): this {
    const processedItems = createDropdownItems<T>(newItems);
    let selected_item = this.state.selected_item;

    if (selectedKey) {
      const found = processedItems.find((item) => item.key === selectedKey);
      if (found) {
        selected_item = found;
      }
    } else if (this.state.placeholder) {
      this.state.status = FORM_DROPDOWN_CONSTANT.INACTIVE;
      selected_item = {
        key: 'no item',
        name: '',
        value: null as unknown as T,
      };
    } else if (
      processedItems.length > 0 &&
      !processedItems.some((item) => item.key === this.state.selected_item.key)
    ) {
      selected_item = processedItems[0] || this.state.selected_item;
    }

    this.setState({ items: processedItems, selected_item });
    this.redraw();

    return this;
  }

  public getSelectedItem(): DropdownItem<T> {
    return this.state.selected_item;
  }

  public getSelectedItemName(): string {
    if (this.state.placeholder && !this.state.selected_item.name) {
      return this.state.placeholder;
    }

    const name = this.getSelectedItem().name;

    if (typeof name === 'string' || name === undefined) {
      return String(name !== undefined ? name : this.getSelectedItem().key);
    } else if (name.make) {
      return name.make(this);
    } else {
      // 해당 컴포넌트를 mui 에서 사용시 발생하는 에러를 방지
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return html(name?._templateStrs, ...name?._templateVals).make(this);
    }
  }

  public setSelectedItem(key: string): boolean {
    const item = this.state.items.find((item) => item.key === key);
    if (item) {
      this.setState({ selected_item: item });
      return true;
    }
    return false;
  }

  public setCustomItemName(name: string | Html): void {
    const customItem = {
      ...this.state.selected_item,
      name,
    };

    this.setState({ selected_item: customItem });
  }

  public setOpen(isOpen: boolean): void {
    if (this.state.is_open !== isOpen) {
      this.setState({ is_open: isOpen });
      this.dispatchEvent(NotifyToggleEvent, { bubbles: true, detail: { is_open: isOpen } });
    }
  }

  public isOpen(): boolean {
    return this.state.is_open;
  }

  public setStatus(
    status:
      | typeof FORM_DROPDOWN_CONSTANT.ACTIVE
      | typeof FORM_DROPDOWN_CONSTANT.INACTIVE
      | typeof FORM_DROPDOWN_CONSTANT.DISABLED
      | typeof FORM_DROPDOWN_CONSTANT.READ_ONLY,
  ) {
    this.state.status = status;
    this.redraw();
  }

  public setHidden(hidden: boolean): this {
    if (hidden) {
      this.element().classList.add(klass.hidden);
    } else {
      this.element().classList.remove(klass.hidden);
    }

    return this;
  }

  /**
   * hidden: true로 설정된 드롭다운 아이템을 생성합니다.
   * 이 메서드를 통해 생성된 아이템은 value 타입 제약을 받지 않습니다.
   *
   * @template U - 드롭다운 아이템의 value 타입
   * @param {Object} item - 생성할 아이템의 속성
   * @returns {DropdownItem<U>} hidden: true로 설정된 드롭다운 아이템
   */
  static hiddenItem<U>(item: { key: string; name?: string | Html; value?: any }): DropdownItem<U> {
    return {
      key: item.key,
      name: item.name ?? item.key,
      value: item.value,
      hidden: true,
    } as DropdownItem<U>;
  }

  setError(is_error: boolean) {
    this.element().dataset.error = String(is_error);
    return this;
  }
}
